import * as z from 'zod';

export const QueryPostCountStatsRequest = z.object({
  options: z
    .object({
      rangeStart: z
        .date()
        .describe(
          'Start of time range to return, in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) date and time format.'
        )
        .optional()
        .nullable(),
      order: z.enum(['UNKNOWN', 'OLDEST', 'NEWEST']).optional(),
      months: z
        .number()
        .int()
        .describe('Number of months to include in response.')
        .min(0)
        .optional(),
      language: z
        .string()
        .describe(
          'Language filter.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nPass a language to only receive the period post count for that specified language.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone to use when calculating the start of the month.\n\n[UTC timezone offset](https://en.wikipedia.org/wiki/List_of_UTC_offsets) format. For example, New York time zone is `-05`.'
        )
        .min(3)
        .max(100)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const QueryPostCountStatsResponse = z.object({
  stats: z
    .array(
      z.object({
        periodStart: z
          .date()
          .describe(
            'Start of time range in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) date and time format.'
          )
          .optional()
          .nullable(),
        postCount: z
          .number()
          .int()
          .describe('Number of posts published during this month.')
          .min(0)
          .optional(),
      })
    )
    .optional(),
});
export const GetTotalPostsRequest = z.object({
  options: z
    .object({
      language: z
        .string()
        .describe(
          'Language filter.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nPass a language to receive the total amount of posts in that specified language.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetTotalPostsResponse = z.object({
  total: z
    .number()
    .int()
    .describe('Total amount of published posts.')
    .min(0)
    .optional(),
});
export const GetPostRequest = z.object({
  postId: z.string().describe('Post ID.').min(1).max(38),
  options: z
    .object({
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT_TEXT',
            'METRICS',
            'SEO',
            'CONTACT_ID',
            'RICH_CONTENT',
            'REFERENCE_ID',
          ])
        )
        .max(20)
        .optional(),
    })
    .optional(),
});
export const GetPostResponse = z.object({
  _id: z
    .string()
    .describe('Post ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  title: z.string().describe('Post title.').max(200).optional(),
  excerpt: z
    .string()
    .describe(
      "Post excerpt.\nCan be selected by a site contributor. By default, it is extracted from the content text's first characters.\n\nMax: 500 characters"
    )
    .max(500)
    .optional(),
  contentText: z
    .string()
    .describe("The post's content in plain text.")
    .max(400000)
    .optional()
    .nullable(),
  firstPublishedDate: z
    .date()
    .describe('Date the post was first published.')
    .optional()
    .nullable(),
  lastPublishedDate: z
    .date()
    .describe('Date the post was last published.')
    .optional()
    .nullable(),
  url: z.string().describe('Post URL.').optional(),
  slug: z
    .string()
    .describe("Post slug. For example, `'post-slug'`.")
    .max(100)
    .optional(),
  featured: z
    .boolean()
    .describe('Whether the post is marked as featured.')
    .optional(),
  pinned: z
    .boolean()
    .describe(
      'Whether the post is pinned. If `true`, the post is placed at the top of the post list.'
    )
    .optional(),
  categoryIds: z.array(z.string()).max(10).optional(),
  memberId: z
    .string()
    .describe("Member ID of the post's owner.")
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  hashtags: z.array(z.string()).max(100).optional(),
  commentingEnabled: z
    .boolean()
    .describe('Whether commenting on the post is enabled.')
    .optional(),
  minutesToRead: z
    .number()
    .int()
    .describe('Estimated reading time (calculated automatically).')
    .optional(),
  heroImage: z
    .string()
    .describe(
      'Image placed at the top of the blog page. Only displays on mobile devices.'
    )
    .optional(),
  tagIds: z.array(z.string()).max(30).optional(),
  relatedPostIds: z.array(z.string()).max(3).optional(),
  pricingPlanIds: z.array(z.string()).max(100).optional(),
  translationId: z
    .string()
    .describe(
      'ID of the translations of this post. All translations of a single post share the same `translationId`.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  language: z
    .string()
    .describe(
      'Language the post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
    )
    .optional()
    .nullable(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('SEO data.')
    .optional(),
  contactId: z
    .string()
    .describe(
      "Contact ID of the post's owner. See the Contacts API for more info."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  richContent: z
    .any()
    .describe(
      'Post rich content\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
    )
    .optional(),
  preview: z
    .boolean()
    .describe(
      'Whether the returned content is a preview of premium content. Defaults to `false`.\nA preview displays a limited number of paragraphs of paid content to non-subscribed users.'
    )
    .optional(),
  moderationDetails: z
    .object({
      submittedBy: z
        .string()
        .describe(
          'Member ID of the person submitting the draft post for review.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      submittedDate: z
        .date()
        .describe('Date the post was submitted for review.')
        .optional()
        .nullable(),
      status: z
        .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
        .describe(
          'Status indicating whether the submission was approved or rejected by the moderator.'
        )
        .optional(),
      moderatedBy: z
        .string()
        .describe('Member ID of the person who approved or rejected the post.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      moderationDate: z
        .date()
        .describe('Date the post was approved or rejected.')
        .optional()
        .nullable(),
    })
    .describe(
      'Post moderation details. Only relevant to posts submitted by [guest writers](https://support.wix.com/en/article/wix-blog-moderating-blog-posts-from-your-guest-writers).\n\nGuest writers have the ability to write posts but not publish them. These posts can be rejected or approved for publishing by a blog editor or Wix user.'
    )
    .optional(),
  media: z
    .intersection(
      z.object({
        displayed: z
          .boolean()
          .describe('Whether cover media is displayed.')
          .optional(),
        custom: z
          .boolean()
          .describe(
            "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
          )
          .optional(),
        altText: z
          .string()
          .describe('Media alternative text.')
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          wixMedia: z.never().optional(),
          embedMedia: z.never().optional(),
        }),
        z.object({
          embedMedia: z.never().optional(),
          wixMedia: z
            .object({
              image: z.string().describe('Image details.').optional(),
              videoV2: z.string().describe('Video details.').optional(),
            })
            .describe('Wix Media details.'),
        }),
        z.object({
          wixMedia: z.never().optional(),
          embedMedia: z
            .object({
              thumbnail: z
                .object({
                  url: z
                    .string()
                    .describe('Thumbnail url.')
                    .max(2000)
                    .optional(),
                  width: z
                    .number()
                    .int()
                    .describe('Thumbnail width.')
                    .min(0)
                    .optional(),
                  height: z
                    .number()
                    .int()
                    .describe('Thumbnail height.')
                    .min(0)
                    .optional(),
                })
                .describe('Thumbnail details.')
                .optional(),
              video: z
                .object({
                  url: z.string().describe('Video url.').max(2000).optional(),
                  width: z
                    .number()
                    .int()
                    .describe('Video width.')
                    .min(0)
                    .optional(),
                  height: z
                    .number()
                    .int()
                    .describe('Video height.')
                    .min(0)
                    .optional(),
                })
                .describe('Video details.')
                .optional(),
            })
            .describe('Embed media details.'),
        }),
      ])
    )
    .describe('Post cover media.')
    .optional(),
  hasUnpublishedChanges: z
    .boolean()
    .describe(
      'Indicates if there is a draft post with changes that have not yet been published.'
    )
    .optional(),
  referenceId: z
    .string()
    .describe(
      'Post reference ID. You can reference the post from other entities, for example, comments.'
    )
    .max(24)
    .optional()
    .nullable(),
});
export const GetPostBySlugRequest = z.object({
  slug: z.string().describe('Slug of the post to retrieve.').min(1).max(200),
  options: z
    .object({
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT_TEXT',
            'METRICS',
            'SEO',
            'CONTACT_ID',
            'RICH_CONTENT',
            'REFERENCE_ID',
          ])
        )
        .max(20)
        .optional(),
      language: z
        .string()
        .describe('Language of the post.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetPostBySlugResponse = z.object({
  post: z
    .object({
      _id: z
        .string()
        .describe('Post ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      title: z.string().describe('Post title.').max(200).optional(),
      excerpt: z
        .string()
        .describe(
          "Post excerpt.\nCan be selected by a site contributor. By default, it is extracted from the content text's first characters.\n\nMax: 500 characters"
        )
        .max(500)
        .optional(),
      contentText: z
        .string()
        .describe("The post's content in plain text.")
        .max(400000)
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      lastPublishedDate: z
        .date()
        .describe('Date the post was last published.')
        .optional()
        .nullable(),
      url: z.string().describe('Post URL.').optional(),
      slug: z
        .string()
        .describe("Post slug. For example, `'post-slug'`.")
        .max(100)
        .optional(),
      featured: z
        .boolean()
        .describe('Whether the post is marked as featured.')
        .optional(),
      pinned: z
        .boolean()
        .describe(
          'Whether the post is pinned. If `true`, the post is placed at the top of the post list.'
        )
        .optional(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Member ID of the post's owner.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the post is enabled.')
        .optional(),
      minutesToRead: z
        .number()
        .int()
        .describe('Estimated reading time (calculated automatically).')
        .optional(),
      heroImage: z
        .string()
        .describe(
          'Image placed at the top of the blog page. Only displays on mobile devices.'
        )
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          'ID of the translations of this post. All translations of a single post share the same `translationId`.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      contactId: z
        .string()
        .describe(
          "Contact ID of the post's owner. See the Contacts API for more info."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Post rich content\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      preview: z
        .boolean()
        .describe(
          'Whether the returned content is a preview of premium content. Defaults to `false`.\nA preview displays a limited number of paragraphs of paid content to non-subscribed users.'
        )
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z
            .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
            .describe(
              'Status indicating whether the submission was approved or rejected by the moderator.'
            )
            .optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Post moderation details. Only relevant to posts submitted by [guest writers](https://support.wix.com/en/article/wix-blog-moderating-blog-posts-from-your-guest-writers).\n\nGuest writers have the ability to write posts but not publish them. These posts can be rejected or approved for publishing by a blog editor or Wix user.'
        )
        .optional(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there is a draft post with changes that have not yet been published.'
        )
        .optional(),
      referenceId: z
        .string()
        .describe(
          'Post reference ID. You can reference the post from other entities, for example, comments.'
        )
        .max(24)
        .optional()
        .nullable(),
    })
    .describe('Retrieved post info.')
    .optional(),
});
export const ListPostsRequest = z.object({
  options: z
    .object({
      featured: z
        .boolean()
        .describe('Whether to return only featured posts.\n\nDefault: `false`')
        .optional(),
      hashtags: z.array(z.string()).max(100).optional(),
      categoryIds: z.array(z.string()).max(50).optional(),
      tagIds: z.array(z.string()).max(50).optional(),
      sort: z
        .enum([
          'FEED',
          'PUBLISHED_DATE_ASC',
          'PUBLISHED_DATE_DESC',
          'VIEW_COUNT',
          'LIKE_COUNT',
          'TITLE_ASC',
          'TITLE_DESC',
        ])
        .optional(),
      paging: z
        .object({
          offset: z
            .number()
            .int()
            .describe(
              'Number of items to skip in the current sort order.\n\n\nDefault: `0`'
            )
            .min(0)
            .optional(),
          limit: z
            .number()
            .int()
            .describe('Number of items to return.\n\n\nDefault:`50`')
            .min(1)
            .max(100)
            .optional(),
          cursor: z
            .string()
            .describe(
              'Pointer to the next or previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
      language: z
        .string()
        .describe(
          'Language filter.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nPass a language to only receive posts that are in that language.\nIf omitted, posts in all languages are returned.'
        )
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe("Post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT_TEXT',
            'METRICS',
            'SEO',
            'CONTACT_ID',
            'RICH_CONTENT',
            'REFERENCE_ID',
          ])
        )
        .max(20)
        .optional(),
    })
    .optional(),
});
export const ListPostsResponse = z.object({
  posts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Post ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        title: z.string().describe('Post title.').max(200).optional(),
        excerpt: z
          .string()
          .describe(
            "Post excerpt.\nCan be selected by a site contributor. By default, it is extracted from the content text's first characters.\n\nMax: 500 characters"
          )
          .max(500)
          .optional(),
        contentText: z
          .string()
          .describe("The post's content in plain text.")
          .max(400000)
          .optional()
          .nullable(),
        firstPublishedDate: z
          .date()
          .describe('Date the post was first published.')
          .optional()
          .nullable(),
        lastPublishedDate: z
          .date()
          .describe('Date the post was last published.')
          .optional()
          .nullable(),
        url: z.string().describe('Post URL.').optional(),
        slug: z
          .string()
          .describe("Post slug. For example, `'post-slug'`.")
          .max(100)
          .optional(),
        featured: z
          .boolean()
          .describe('Whether the post is marked as featured.')
          .optional(),
        pinned: z
          .boolean()
          .describe(
            'Whether the post is pinned. If `true`, the post is placed at the top of the post list.'
          )
          .optional(),
        categoryIds: z.array(z.string()).max(10).optional(),
        memberId: z
          .string()
          .describe("Member ID of the post's owner.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        hashtags: z.array(z.string()).max(100).optional(),
        commentingEnabled: z
          .boolean()
          .describe('Whether commenting on the post is enabled.')
          .optional(),
        minutesToRead: z
          .number()
          .int()
          .describe('Estimated reading time (calculated automatically).')
          .optional(),
        heroImage: z
          .string()
          .describe(
            'Image placed at the top of the blog page. Only displays on mobile devices.'
          )
          .optional(),
        tagIds: z.array(z.string()).max(30).optional(),
        relatedPostIds: z.array(z.string()).max(3).optional(),
        pricingPlanIds: z.array(z.string()).max(100).optional(),
        translationId: z
          .string()
          .describe(
            'ID of the translations of this post. All translations of a single post share the same `translationId`.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Language the post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        contactId: z
          .string()
          .describe(
            "Contact ID of the post's owner. See the Contacts API for more info."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        richContent: z
          .any()
          .describe(
            'Post rich content\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        preview: z
          .boolean()
          .describe(
            'Whether the returned content is a preview of premium content. Defaults to `false`.\nA preview displays a limited number of paragraphs of paid content to non-subscribed users.'
          )
          .optional(),
        moderationDetails: z
          .object({
            submittedBy: z
              .string()
              .describe(
                'Member ID of the person submitting the draft post for review.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            submittedDate: z
              .date()
              .describe('Date the post was submitted for review.')
              .optional()
              .nullable(),
            status: z
              .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
              .describe(
                'Status indicating whether the submission was approved or rejected by the moderator.'
              )
              .optional(),
            moderatedBy: z
              .string()
              .describe(
                'Member ID of the person who approved or rejected the post.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            moderationDate: z
              .date()
              .describe('Date the post was approved or rejected.')
              .optional()
              .nullable(),
          })
          .describe(
            'Post moderation details. Only relevant to posts submitted by [guest writers](https://support.wix.com/en/article/wix-blog-moderating-blog-posts-from-your-guest-writers).\n\nGuest writers have the ability to write posts but not publish them. These posts can be rejected or approved for publishing by a blog editor or Wix user.'
          )
          .optional(),
        media: z
          .intersection(
            z.object({
              displayed: z
                .boolean()
                .describe('Whether cover media is displayed.')
                .optional(),
              custom: z
                .boolean()
                .describe(
                  "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                )
                .optional(),
              altText: z
                .string()
                .describe('Media alternative text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z.never().optional(),
              }),
              z.object({
                embedMedia: z.never().optional(),
                wixMedia: z
                  .object({
                    image: z.string().describe('Image details.').optional(),
                    videoV2: z.string().describe('Video details.').optional(),
                  })
                  .describe('Wix Media details.'),
              }),
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z
                  .object({
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Thumbnail details.')
                      .optional(),
                    video: z
                      .object({
                        url: z
                          .string()
                          .describe('Video url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Video width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Video height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Video details.')
                      .optional(),
                  })
                  .describe('Embed media details.'),
              }),
            ])
          )
          .describe('Post cover media.')
          .optional(),
        hasUnpublishedChanges: z
          .boolean()
          .describe(
            'Indicates if there is a draft post with changes that have not yet been published.'
          )
          .optional(),
        referenceId: z
          .string()
          .describe(
            'Post reference ID. You can reference the post from other entities, for example, comments.'
          )
          .max(24)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metaData: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in this response.')
        .optional(),
      offset: z.number().int().describe('Requested offset.').optional(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional(),
      cursor: z
        .string()
        .describe(
          'Pointer to the next or previous page in the list of results.'
        )
        .max(2000)
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const QueryPostsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          title: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          firstPublishedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          lastPublishedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          slug: z
            .object({
              $eq: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          featured: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          pinned: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          categoryIds: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          memberId: z
            .object({
              $eq: z.string(),
              $hasSome: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          hashtags: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          commentingEnabled: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          minutesToRead: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          tagIds: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          pricingPlanIds: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          language: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          translationId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'media.displayed': z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'title',
                'firstPublishedDate',
                'lastPublishedDate',
                'slug',
                'featured',
                'pinned',
                'commentingEnabled',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
  options: z
    .object({
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT_TEXT',
            'METRICS',
            'SEO',
            'CONTACT_ID',
            'RICH_CONTENT',
            'REFERENCE_ID',
          ])
        )
        .max(20)
        .optional(),
    })
    .optional(),
});
export const QueryPostsResponse = z.object({
  posts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Post ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        title: z.string().describe('Post title.').max(200).optional(),
        excerpt: z
          .string()
          .describe(
            "Post excerpt.\nCan be selected by a site contributor. By default, it is extracted from the content text's first characters.\n\nMax: 500 characters"
          )
          .max(500)
          .optional(),
        contentText: z
          .string()
          .describe("The post's content in plain text.")
          .max(400000)
          .optional()
          .nullable(),
        firstPublishedDate: z
          .date()
          .describe('Date the post was first published.')
          .optional()
          .nullable(),
        lastPublishedDate: z
          .date()
          .describe('Date the post was last published.')
          .optional()
          .nullable(),
        url: z.string().describe('Post URL.').optional(),
        slug: z
          .string()
          .describe("Post slug. For example, `'post-slug'`.")
          .max(100)
          .optional(),
        featured: z
          .boolean()
          .describe('Whether the post is marked as featured.')
          .optional(),
        pinned: z
          .boolean()
          .describe(
            'Whether the post is pinned. If `true`, the post is placed at the top of the post list.'
          )
          .optional(),
        categoryIds: z.array(z.string()).max(10).optional(),
        memberId: z
          .string()
          .describe("Member ID of the post's owner.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        hashtags: z.array(z.string()).max(100).optional(),
        commentingEnabled: z
          .boolean()
          .describe('Whether commenting on the post is enabled.')
          .optional(),
        minutesToRead: z
          .number()
          .int()
          .describe('Estimated reading time (calculated automatically).')
          .optional(),
        heroImage: z
          .string()
          .describe(
            'Image placed at the top of the blog page. Only displays on mobile devices.'
          )
          .optional(),
        tagIds: z.array(z.string()).max(30).optional(),
        relatedPostIds: z.array(z.string()).max(3).optional(),
        pricingPlanIds: z.array(z.string()).max(100).optional(),
        translationId: z
          .string()
          .describe(
            'ID of the translations of this post. All translations of a single post share the same `translationId`.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Language the post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        contactId: z
          .string()
          .describe(
            "Contact ID of the post's owner. See the Contacts API for more info."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        richContent: z
          .any()
          .describe(
            'Post rich content\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        preview: z
          .boolean()
          .describe(
            'Whether the returned content is a preview of premium content. Defaults to `false`.\nA preview displays a limited number of paragraphs of paid content to non-subscribed users.'
          )
          .optional(),
        moderationDetails: z
          .object({
            submittedBy: z
              .string()
              .describe(
                'Member ID of the person submitting the draft post for review.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            submittedDate: z
              .date()
              .describe('Date the post was submitted for review.')
              .optional()
              .nullable(),
            status: z
              .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
              .describe(
                'Status indicating whether the submission was approved or rejected by the moderator.'
              )
              .optional(),
            moderatedBy: z
              .string()
              .describe(
                'Member ID of the person who approved or rejected the post.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            moderationDate: z
              .date()
              .describe('Date the post was approved or rejected.')
              .optional()
              .nullable(),
          })
          .describe(
            'Post moderation details. Only relevant to posts submitted by [guest writers](https://support.wix.com/en/article/wix-blog-moderating-blog-posts-from-your-guest-writers).\n\nGuest writers have the ability to write posts but not publish them. These posts can be rejected or approved for publishing by a blog editor or Wix user.'
          )
          .optional(),
        media: z
          .intersection(
            z.object({
              displayed: z
                .boolean()
                .describe('Whether cover media is displayed.')
                .optional(),
              custom: z
                .boolean()
                .describe(
                  "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                )
                .optional(),
              altText: z
                .string()
                .describe('Media alternative text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z.never().optional(),
              }),
              z.object({
                embedMedia: z.never().optional(),
                wixMedia: z
                  .object({
                    image: z.string().describe('Image details.').optional(),
                    videoV2: z.string().describe('Video details.').optional(),
                  })
                  .describe('Wix Media details.'),
              }),
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z
                  .object({
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Thumbnail details.')
                      .optional(),
                    video: z
                      .object({
                        url: z
                          .string()
                          .describe('Video url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Video width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Video height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Video details.')
                      .optional(),
                  })
                  .describe('Embed media details.'),
              }),
            ])
          )
          .describe('Post cover media.')
          .optional(),
        hasUnpublishedChanges: z
          .boolean()
          .describe(
            'Indicates if there is a draft post with changes that have not yet been published.'
          )
          .optional(),
        referenceId: z
          .string()
          .describe(
            'Post reference ID. You can reference the post from other entities, for example, comments.'
          )
          .max(24)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(2000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const GetPostMetricsRequest = z.object({
  postId: z
    .string()
    .describe('Post ID to retrieve metrics for.')
    .min(1)
    .max(38),
});
export const GetPostMetricsResponse = z.object({
  metrics: z
    .object({
      comments: z
        .number()
        .int()
        .describe('Total number of post comments.')
        .optional(),
      likes: z
        .number()
        .int()
        .describe('Total number of post likes.')
        .optional(),
      views: z
        .number()
        .int()
        .describe('Total number of post views.')
        .optional(),
    })
    .describe('Retrieved post metrics.')
    .optional(),
});
